var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { ChartProvider, ChartWidget, CurrentPatientProvider, DeviceProvider, MeasurementCommentProvider, Spinner, ThresholdsProtocolsProvider, } from "@madmedical/ui";
import { adaptThresholdsProtocol, useGetThresholdsProtocolsQuery, } from "@madmedical/threshold";
import { invariant, rangeToDate, rangeToString } from "@madmedical/utils";
import { adaptPatient, usePatient } from "@madmedical/user";
import { useDevices } from "@madmedical/device";
import { useDateRange } from "@madmedical/store";
import { useEffect } from "react";
import { useSearchParams } from "@madmedical/routing";
import { useGetMeasurementRangeQuery } from "../api";
import ChartDecorated from "../../charts/ChartDecorated";
import { useGetCommentsQuery } from "../../comment/api";
const MeasurementProviders = ({ metricType, statsRange, selectedRange, onSelectRange, children, }) => {
    const { patientId, patient, isLoading: isPatientLoading } = usePatient();
    const { devices, isLoading: isDevicesLoading } = useDevices();
    const { dateRange, setDateRange } = useDateRange();
    const [searchParams] = useSearchParams();
    useEffect(() => {
        const from = searchParams.get("from");
        const to = searchParams.get("to");
        if (!from || !to) {
            return;
        }
        setDateRange({ from, to });
    }, [searchParams, setDateRange]);
    const { data: chartData, isLoading: isMeasurementsLoading } = useGetMeasurementRangeQuery({
        userId: patientId,
        metricType,
        queryRange: rangeToString(dateRange),
        statsRange: rangeToString(statsRange !== null && statsRange !== void 0 ? statsRange : dateRange),
    });
    const { data: thresholdsProtocols, isLoading: isThresholdsProtocolsLoading, } = useGetThresholdsProtocolsQuery(patientId);
    const { data: commentsData, isLoading: isCommentsLoading } = useGetCommentsQuery({
        patientId,
        dateRange: rangeToString(dateRange),
    });
    if (isDevicesLoading ||
        isMeasurementsLoading ||
        isPatientLoading ||
        isThresholdsProtocolsLoading ||
        isCommentsLoading) {
        return _jsx(Spinner, {});
    }
    invariant(chartData);
    invariant(patient);
    invariant(thresholdsProtocols);
    const { worstAlertLevel, displayRange, unit, providers } = chartData.stats;
    return (_jsx(ChartProvider, Object.assign({ value: {
            chart: (_jsx(ChartDecorated, { metricType: metricType, data: chartData, dateRange: dateRange, widget: ChartWidget.Page })),
            metricType,
            dateRange: statsRange ? rangeToDate(statsRange) : dateRange,
            displayRange: displayRange ? `${displayRange} ${unit}` : null,
            alertLevel: worstAlertLevel,
            setDateRange,
            selectedRange,
            onSelectRange,
        } }, { children: _jsx(DeviceProvider, Object.assign({ value: {
                devices: devices.filter((device) => providers.includes(device.provider)),
            } }, { children: _jsx(CurrentPatientProvider, Object.assign({ value: { patient: adaptPatient(patient) } }, { children: _jsx(ThresholdsProtocolsProvider, Object.assign({ value: {
                        thresholdsProtocols: thresholdsProtocols
                            .filter((thresholdsProtocol) => thresholdsProtocol.metricType ===
                            metricType)
                            .map(adaptThresholdsProtocol),
                    } }, { children: _jsx(MeasurementCommentProvider, Object.assign({ value: {
                            comments: commentsData
                                ? commentsData.map((_a) => {
                                    var { date, author } = _a, rest = __rest(_a, ["date", "author"]);
                                    return (Object.assign({ date: new Date(date), author: Object.assign(Object.assign({}, author), { isSelf: patientId === author.id }) }, rest));
                                })
                                : [],
                            onDelete: () => null,
                        } }, { children: children })) })) })) })) })));
};
export default MeasurementProviders;
